import { useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import type { Feature, FeatureCollection, Polygon } from 'geojson';

import useCircleDraw from './hooks/useCircleDraw';
import useDrawingCursor from './hooks/useDrawingCursor';
import useEditableShape from './hooks/useEditableShape';
import useGrabTool from './hooks/useGrabTool';
import usePointDraw from './hooks/usePointDraw';
import usePolygonDraw from './hooks/usePolygonDraw';
import useProjectionOverlay from './hooks/useProjectionOverlay';
import { DrawingMode, Interaction, toFeatureCollection } from './geometry';

type DrawingLayerProps = {
  features: FeatureCollection;
  onChange: (next: FeatureCollection) => void;
  mode: Interaction;
  onGrab?: (circle: Feature<Polygon>) => void;
  lineColor?: string;
};

const DrawingLayer = ({
  features,
  onChange,
  mode,
  onGrab,
  lineColor,
}: DrawingLayerProps) => {
  const map = useMap();

  const hasShape = features.features.length > 0;
  // Non-null only while armed to draw: a draw mode is selected and no shape
  // exists yet. Once a shape is committed the layer switches to editing it.
  const drawKind: DrawingMode | null =
    (mode === 'circle' || mode === 'polygon' || mode === 'linestring') &&
    !hasShape
      ? mode
      : null;

  const pointArmed = mode === 'point';

  const {
    committedRef,
    makeEditable,
    commitNow,
    overlayToFeature,
    destroyShape,
    seedShape,
  } = useEditableShape(onChange);

  const projectionOverlayRef = useProjectionOverlay(map);

  // Clear the committed shape when the consumer empties features.
  useEffect(() => {
    if (features.features.length === 0) {
      destroyShape();
    }
  }, [features, destroyShape]);

  useEffect(() => {
    if (drawKind) {
      destroyShape();
    }
  }, [drawKind, destroyShape]);

  // Tear the committed overlay down on unmount, else the imperative shape orphans.
  useEffect(() => () => destroyShape(), [destroyShape]);

  // Seed a consumer-provided geometry as editable.
  useEffect(() => {
    if (
      map &&
      hasShape &&
      !committedRef.current &&
      features.features[0]?.geometry.type !== 'Point'
    ) {
      seedShape(map, features.features[0] as Feature, lineColor);
    }
  }, [map, hasShape, features, committedRef, seedShape, lineColor]);

  useDrawingCursor({ map, mode, drawKind });

  usePointDraw({
    map,
    enabled: pointArmed,
    onCommit: feature => onChange(toFeatureCollection(feature)),
  });

  useCircleDraw({
    map,
    enabled: drawKind === 'circle',
    projectionOverlayRef,
    committedRef,
    makeEditable,
    overlayToFeature,
    commitNow,
  });

  usePolygonDraw({
    map,
    kind: drawKind === 'polygon' || drawKind === 'linestring' ? drawKind : null,
    projectionOverlayRef,
    committedRef,
    makeEditable,
    commitNow,
    lineColor,
  });

  useGrabTool({ map, enabled: mode === 'grab', onGrab });

  return null;
};

export default DrawingLayer;
